/**
 * WhatCartieCanDoNow.js  —  v34.91 (Feb 13 2026)
 *
 * "What CARTIE can do RIGHT NOW" — 3-card outcome row.
 *
 * Sits directly under the Hero's trust strip, above "Why CARTIE is
 * different". Purely additive — replaces no existing component.
 *
 * Tracks card clicks via the existing useAnalytics batched pipeline
 * (event_type='now_card_click', card_id=...) so the founder can see
 * which of the 3 discovery entry points converts best.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Coins, MessageSquare, ArrowRight } from 'lucide-react';
import { trackNowCardClick } from '../hooks/useAnalytics';

const CARDS = [
  {
    title: 'Audit any cloud bill in 60 seconds',
    sub: 'AWS, Azure, GCP, Snowflake, Databricks, DigitalOcean. Drop a CSV → top 5 leaks.',
    href: '/cloud-bill-audit',
    icon: Activity,
    accent: 'emerald',
    cta: 'Pick your cloud',
    testid: 'now-card-reality-check',
    trackId: 'cloud_bill_audit_hub',
  },
  {
    title: 'Find the $ hiding in your token bill',
    sub: 'Drop a prompt. See cost per provider before you ship.',
    href: '/prompt-cost-predictor',
    icon: Coins,
    accent: 'cyan',
    cta: 'Predict token cost',
    testid: 'now-card-prompt-cost',
    trackId: 'prompt_cost',
  },
  {
    title: 'Ask CARTIE in Slack or Teams: "why did my bill spike?"',
    sub: 'Pick your platform. Get a real answer in <10 seconds.',
    href: '/chat-bot',
    icon: MessageSquare,
    accent: 'violet',
    cta: 'Install the bot',
    testid: 'now-card-slack-bot',
    trackId: 'chat_bot',
  },
];

const ACCENTS = {
  emerald: {
    // v34.99 (Feb 14 2026) — Solid gradient backgrounds + white icons.
    // Old style (bg-emerald-500/15) looked dim/dull on the dark hero. New
    // pattern matches the Careers page "Honest Note" heart-icon aesthetic:
    // saturated gradient + crisp white glyph + colored glow.
    iconBg: 'bg-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-500',
    iconRing: 'ring-emerald-400/50',
    iconGlow: 'shadow-emerald-500/40',
    iconColor: 'text-white',
    cta: 'text-emerald-300 group-hover:text-emerald-200',
    border: 'hover:border-emerald-500/40',
  },
  cyan: {
    iconBg: 'bg-cyan-500 bg-gradient-to-br from-cyan-500 to-sky-500',
    iconRing: 'ring-cyan-400/50',
    iconGlow: 'shadow-cyan-500/40',
    iconColor: 'text-white',
    cta: 'text-cyan-300 group-hover:text-cyan-200',
    border: 'hover:border-cyan-500/40',
  },
  violet: {
    iconBg: 'bg-violet-500 bg-gradient-to-br from-violet-500 to-fuchsia-500',
    iconRing: 'ring-violet-400/50',
    iconGlow: 'shadow-violet-500/40',
    iconColor: 'text-white',
    cta: 'text-violet-300 group-hover:text-violet-200',
    border: 'hover:border-violet-500/40',
  },
};

export default function WhatCartieCanDoNow() {
  return (
    <section
      className="relative px-4 py-14 sm:py-16 bg-slate-950 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900/60"
      data-testid="what-cartie-can-do-now"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header — small, no shouting */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-2">
              What CARTIE can do right now
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Three working entry points. <span className="text-slate-400 font-normal">No signup.</span>
            </h2>
          </div>
          <Link
            to="/free-tools"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-200 font-medium self-start sm:self-end"
            data-testid="now-row-see-all"
          >
            See all free tools
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* 3-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CARDS.map((card) => {
            const a = ACCENTS[card.accent];
            const Icon = card.icon;
            return (
              <Link
                key={card.href}
                to={card.href}
                data-testid={card.testid}
                onClick={() => trackNowCardClick(card.trackId)}
                className={`group relative block rounded-2xl bg-white/[0.03] border border-white/10 ${a.border} p-6 transition-all duration-300 hover:bg-white/[0.05] hover:-translate-y-0.5`}
              >
                {/* v34.99 (Feb 14 2026) — Vibrant gradient icon badge.
                    Was `bg-{accent}-500/15` (15% opacity → dim). Now solid
                    gradient + white icon + colored glow. Same aesthetic as
                    the Careers "Honest Note" heart icon. */}
                <div
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${a.iconBg} ring-1 ${a.iconRing} shadow-lg ${a.iconGlow} mb-4`}
                >
                  <Icon className={`w-5 h-5 ${a.iconColor}`} strokeWidth={2.4} />
                </div>

                <h3 className="text-base font-semibold text-white mb-1.5 leading-snug">
                  {card.title}
                </h3>
                <p className="text-sm text-slate-300 mb-5 leading-relaxed">{card.sub}</p>

                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold ${a.cta} transition-colors`}
                >
                  {card.cta}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
